// Curved infinite collection scroller.
// The track is a native horizontal scroller (swipe, drag-scroll, keyboard). Items are cloned on
// both sides so scrolling never runs out; when the view drifts past one full set it jumps back by
// exactly one set width, which is invisible. Each frame, items are lowered, tilted and scaled by
// their distance from the centre so the row bends into an arc and the centre card is largest.
// It drifts slowly on its own (unless reduced motion is on) and pauses on hover, focus or touch.
// Prev/next buttons (--prev / --next commands) step one card.
class CurveCarousel extends HTMLElement {
  #controller = null;
  #frame = 0;
  #paused = false;
  #resumeTimer = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.track = this.querySelector('[data-curve-track]');
    if (!this.track) return;
    this.originals = Array.from(this.track.querySelectorAll('[data-curve-item]'));
    if (this.originals.length === 0) return;

    // In the theme editor, the loop copies keep the card's editor link and stay clickable, so clicking
    // any copy selects its real card (and its Cover image setting) instead of the container.
    this.designMode = this.hasAttribute('data-design-mode') || Boolean(window.Shopify?.designMode);
    this.#cloneSets();
    this.speed = Number(this.dataset.speed || 0.35);
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    requestAnimationFrame(() => {
      this.track.scrollLeft = this.#setWidth();
      this.#tick();
    });

    const pause = () => {
      this.#paused = true;
      clearTimeout(this.#resumeTimer);
    };
    const resumeSoon = () => {
      clearTimeout(this.#resumeTimer);
      this.#resumeTimer = setTimeout(() => (this.#paused = false), 1800);
    };
    this.addEventListener('pointerenter', pause, { signal });
    this.addEventListener('pointerleave', resumeSoon, { signal });
    this.addEventListener('focusin', pause, { signal });
    this.addEventListener('focusout', resumeSoon, { signal });
    this.track.addEventListener('touchstart', pause, { passive: true, signal });
    this.track.addEventListener('touchend', resumeSoon, { passive: true, signal });
    this.addEventListener(
      'command',
      (event) => {
        if (event.command === '--prev') this.#step(-1);
        if (event.command === '--next') this.#step(1);
      },
      { signal }
    );
    document.addEventListener('visibilitychange', () => (document.hidden ? pause() : resumeSoon()), { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#resumeTimer);
  }

  #cloneSets() {
    const makeClones = () =>
      this.originals.map((item) => {
        const clone = item.cloneNode(true);
        clone.dataset.curveClone = '';
        if (this.designMode) return clone;
        clone.removeAttribute('data-shopify-editor-block');
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        return clone;
      });
    this.track.prepend(...makeClones());
    this.track.append(...makeClones());
    this.items = Array.from(this.track.querySelectorAll('[data-curve-item]'));
  }

  #setWidth() {
    const first = this.originals[0];
    const firstClone = this.items[0];
    return first.offsetLeft - firstClone.offsetLeft;
  }

  #step(direction) {
    const item = this.originals[0];
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    this.#paused = true;
    this.track.scrollBy({ left: direction * (item.offsetWidth + gap), behavior: this.reduceMotion ? 'auto' : 'smooth' });
    clearTimeout(this.#resumeTimer);
    this.#resumeTimer = setTimeout(() => (this.#paused = false), 2500);
  }

  #tick = () => {
    const track = this.track;
    const setWidth = this.#setWidth();
    if (!this.#paused && !this.reduceMotion) track.scrollLeft += this.speed;
    if (setWidth > 0) {
      if (track.scrollLeft >= setWidth * 2) track.scrollLeft -= setWidth;
      if (track.scrollLeft <= 1) track.scrollLeft += setWidth;
    }

    const rect = track.getBoundingClientRect();
    const centre = rect.left + rect.width / 2;
    const half = rect.width / 2 || 1;
    const rtl = getComputedStyle(this).direction === 'rtl';
    let nearest = null;
    let nearestDistance = Infinity;
    for (const item of this.items) {
      const box = item.getBoundingClientRect();
      if (box.right < rect.left - box.width || box.left > rect.right + box.width) continue;
      const offset = (box.left + box.width / 2 - centre) / half; // -1 … 1 across the view
      const distance = Math.min(Math.abs(offset), 1.4);
      const lift = distance * distance * 64;
      const tilt = Math.max(-14, Math.min(14, offset * 12)) * (rtl ? -1 : 1);
      const scale = 1.12 - Math.min(distance, 1) * 0.2;
      item.style.transform = `translateY(${lift}px) rotate(${tilt}deg) scale(${scale})`;
      item.style.zIndex = String(100 - Math.round(distance * 50));
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearest = item;
      }
    }
    // Only the card closest to the centre shows its name.
    if (nearest !== this.centreItem) {
      this.centreItem?.removeAttribute('data-centre');
      nearest?.setAttribute('data-centre', '');
      this.centreItem = nearest;
    }
    this.#frame = requestAnimationFrame(this.#tick);
  };
}

if (!customElements.get('s-curve-carousel')) {
  customElements.define('s-curve-carousel', CurveCarousel);
}
